import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { drillReportService } from 'api/services/drillReportService';
import notificationService from 'services/notificationService';
import type { DrillQueryRequest, DrillRow, DrillSummary } from 'api/types/drillReport.types';
import type { PaginatedResponse } from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1, pageSize: 25, totalPages: 0, totalItems: 0,
  hasNextPage: false, hasPreviousPage: false,
};

export const useDrillReport = (params?: DrillQueryRequest) => {
  const [data, setData] = useState<DrillRow[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<DrillRow>['pagination']>(EMPTY_PAGINATION);
  const [summary, setSummary] = useState<DrillSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  // Show toasts at most once per unique error message — avoids duplicate
  // toasts when both list+summary requests fail with the same 403.
  const lastToastedRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!params) return;
    setLoading(true); setError(null);
    try {
      const [listRes, summaryRes] = await Promise.all([
        drillReportService.getDrill(params),
        drillReportService.getDrillSummary(params),
      ]);
      setData(listRes.data); setPagination(listRes.pagination);
      setSummary(summaryRes);
      lastToastedRef.current = null;
    } catch (err: any) {
      const status = err?.response?.status;
      const resp = err?.response?.data;
      const apiMsg = resp?.message
        || (Array.isArray(resp?.errors) && resp.errors.length > 0 ? resp.errors.join('; ') : null);
      const fallback = err?.message || 'Failed to load report data.';
      const finalMessage = apiMsg || fallback;

      console.error('Drill report fetch error:', status, resp || err?.message);
      setError(err as Error);
      // Reset list/pagination so a 403 collapses to an empty grid.
      setData([]);
      setPagination(EMPTY_PAGINATION);

      // Show the API's actual message — especially valuable for 403 scope-violation errors.
      if (lastToastedRef.current !== finalMessage) {
        lastToastedRef.current = finalMessage;
        if (status === 403) {
          notificationService.toast.error(finalMessage, 8000);
        } else if (status >= 500) {
          notificationService.toast.error(finalMessage);
        }
        // 4xx other than 403: silent (often a benign filter combo)
      }
    }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, pagination, summary, loading, error, refetch: fetchData };
};
